import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { INITIAL_HINT_COUNT } from '../constants';
import { getWeekIndex } from '../utils';
import { getTreeCount } from '../backend';
import pageStyles from '../styles/page-common.module.css';


function FAQs() {

  const [treeCount, setTreeCount] = useState('[counting trees...]');

  // get tree count on mount
  useEffect(() => {
    const countTrees = async () => {
      try {
        const response = await getTreeCount({'language': 'ja'});
        setTreeCount(response);
      } catch (error) {
        console.error('Error fetching tree count:', error);
        setTreeCount('[error counting trees!]');
      }
    };
    countTrees();
  }, []);

  return (
    <>
      <Helmet>
        <title>FAQs | LingoLeaf</title>
        <meta name="description" content="Find answers to commonly asked questions about LingoLeaf, our subscription options, and how our story-based language learning methodology works." />
      </Helmet>
      <div className={pageStyles.styledPageLeftAligned}>
        <h1>FAQs</h1>
        
        <h2>What is LingoLeaf?</h2>
        <p>LingoLeaf is a web app hosting a variety of fun and engaging short stories in different languages and different difficulty levels, alongside helpful translations and annotations.</p>
        <p>Use LingoLeaf to develop your reading skills in the language you want to learn.</p>
        <br/>
        
        <h2>How does it work?</h2>
        <p>Each story on LingoLeaf is called a 'tree'. Each tree is divided into seven 'leaves' (think: chapters).</p>
        <p>When you read a leaf, you can click on individual sentences to see a translation and an explanation of the vocabulary and grammar. You can do this only a set number of times (per leaf) before you run out of hints; the idea is that you should try as much as possible to read without translation.</p>
        <p>When you've finished reading, you'll answer some comprehension questions about the story. These quizzes aren't graded; you will still 'complete' the leaf if you get all the questions wrong.</p>
        <p>Complete all 7 leaves to complete a tree!</p>
        <br/>

        <h2>How many trees/stories are there?</h2>
        <p>As of today ({(new Date()).toDateString()}), there are {String(treeCount)} stories per language (so {String(treeCount/3)} stories per language for each difficulty level).</p>
        <br/>

        <h2>How often are new trees/stories released?</h2>
        <p>New trees (stories) are released on a very regular basis, at each language and each difficulty level (Beginner/Intermediate/Hard). The aim is to have a new story for each language and level weekly, unless we are busy working on a different area of LingoLeaf in which case we might slow down the release calendar.</p>
        <br/>

        <h2>What is my "streak" and how is it calculated?</h2>
        <p>Unlike many similar services which calculate a daily streak, on LingoLeaf you have a <strong>weekly</strong> streak. A week starts at the beginning of Monday and ends at the end of Sunday. If you collect a tree (i.e. complete all 7 leaves) at some point during the week, then your streak will increase by one. If you miss a week, your streak resets to zero. If your streak is say, 11, that means you have completed trees the last 11 weeks in a row.</p>
        <p>When you are logged in, your streak is indicated by the number next to the lightning bolt symbol in the header bar. The lightning bolt will appear grey if you have not yet completed a tree this week.</p>
        <p>In principle, you could maintain your streak by doing nothing Monday to Saturday, then hurriedly going through a whole tree on Sunday evening. However, we would recommend spacing out the 7 leaves over the week, e.g., one every day. You learn better by studying little and often!</p>
        <br/>

        <h2>Which languages can I learn?</h2>
        <p>See the <NavLink to='/languages'>Languages</NavLink> page.</p>
        <br/>

        <h2>How difficult is the reading material?</h2>
        <p>Each language on LingoLeaf has 3 difficulty levels: Beginner, Intermediate, Advanced. The aim is for <a rel="noreferrer" target="_blank" href="https://en.wikipedia.org/wiki/Common_European_Framework_of_Reference_for_Languages">CEFR</a> A1 content at the Beginner level, B1 at the Intermediate level,  C2 at the Advanced level..</p>
        <p>Each week, new trees are released at each language and at each difficulty level.</p>
        <br/>

        <h2>Why can't I click on sentences anymore?</h2>
        <p>Pre-quiz, you can only click on sentences to get translations/explanations {INITIAL_HINT_COUNT} times before you run out of these hints. The idea is that you should try as much as possible to read without translation. During the quiz, you can't click on sentences, even if you had hints remaining. Post-quiz, you can click on sentences to your heart's content.</p>
        <br/>

        <h2>I got a question wrong, can I redo the leaf?</h2>
        <p>Only by resetting the whole tree with the 'Reset Tree' button on the tree page.</p>
        <br/>

        <h2>Where do the stories come from?</h2>
        <p>The stories are created using a novel human-AI feedback loop. We (humans) provide a story idea to an AI, which then responds with a story draft. We then provide feedback and suggestions for a second draft, and so on, until a story is created. In short, we do a lot of careful thinking and editing to make sure the generated stories are novel and engaging, while remaining at the desired difficulty level.</p>
        <br/>

        <h2>In Japanese, why do some of the kanji characters have pronunciations (furigana) above and others don't?</h2>
        <p>At each LingoLeaf difficulty level, we only give pronunciations for words that contain kanji that are too advanced for a reader at that level.</p>
        <p>In particular, to judge which kanji are appropriate (i.e., don't need furigana) for each difficulty level, we followed <a rel="noreferrer" target="_blank" href="https://www.wanikani.com/">WaniKani</a>'s kanji lists: we took WaniKani levels 1-10 for Beginner, 1-30 for Intermediate, and 1-60 for Advanced. This is ~350, ~1000, ~2000 kanji respectively, so very roughly comparable to JLPT levels N4, N2, and N1.</p>
        <p>Note that we use KAKASI to generate furigana for our stories, which isn't always perfect!</p>
        <br/>

        <h2>Are the stories appropriate for children?</h2>
        <p>Although the stories are AI-generated, there is human moderation to ensure there is no excessively violent, sexually explicit, or offensive content in our stories. If you encounter any such material, please report it to us as soon as possible and we will remove it. That being said, please bear in mind that the stories can sometimes be a little scary or feature some grown-up themes such as loss or discrimination or hardship.</p>
        <p>Also, remember that our <NavLink to='/terms'>terms</NavLink> prohibit user accounts for any users under 13. People younger than 13 can only use LingoLeaf with adult supervision. We leave it up to the parents or caregivers to judge whether a given story is suitable for their child.</p>
        <br/>

        <h2>How much does it cost?</h2>
        <p>There is a 2 week free trial, then a very small monthly subscription. See the <NavLink to='/pricing'>Pricing</NavLink> page for more details.</p>
        <br/>

        <h2>What emails and notifications will I receive from LingoLeaf, and how do I update my preferences?</h2>
        <p>In accordance with our <NavLink to='/privacy'>privacy policy</NavLink>, we send three types of communications via email: essential communications (e.g., with important updates or information about your subscription), marketing communications (e.g., with information about special promotions), and study reminders. Consenting to essential communications is a requirement when signing up for an account on LingoLeaf, but the other two kinds of email are optional. There are checkboxes for these when you sign up, and you can change your settings later via the Settings interface.</p>
        <br/>

        <h2>I signed up for marketing communications why haven't I received any?</h2>
        <p>We haven't started sending these out yet! We're still very new. We nonetheless asked for your consent when you signed up because we plan to start sending these in the future.</p>
        <br/>

        <h2>How do I delete my account / cancel my subscription?</h2>
        <p>You can delete your account at any time (including during the free trial) within the Settings menu when logged in. Your subscription payments will not continue. You can choose to delete your account either immediately or at the end of your current subscription month.</p>
        <p>Alternatively, our payment provider (Paddle) will send you email receipts, which will contain links to cancel your subscription payments. Cancelling your payments this way will also delete your LingoLeaf account.</p>
        <br/>

        <h2>Who made LingoLeaf?</h2>
        <p>See the <NavLink to='/about'>About LingoLeaf</NavLink> page.</p>
        <br/>

        <h2>What's new with LingoLeaf?</h2>
        <p>Have a look at the <NavLink to='/news'>News</NavLink> page.</p>
        <br/>

        <h2>When will you add feature X / language Y to LingoLeaf?</h2>
        <p>We're still very new and we have lots of plans for new features and languages to add. If there is something in particular you would like to see, take a look at our <NavLink to='/future'>Future Plans</NavLink> page to see if we're already planning to implement it. Otherwise, please <NavLink to='/contact'>get in touch</NavLink>!</p>
        <br/>

        <h2>Where do actual trees fit in?</h2>
        <p>By using LingoLeaf, you are <NavLink to='/charity'>planting real trees</NavLink> in the real world.</p>
        <br/>

      </div>
    </>
  );
}

export default FAQs;